import styled, { css } from "styled-components";
import vars from "../../styles/vars";
import { responsiveUnit } from "../../utils";

const CardSideStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 0.7rem 1.5rem;
  backface-visibility: hidden;
  transition: transform 1s ease-in-out;

  > div:first-child {
    height: 100%;
  }

  > div > p {
    padding-bottom: 0;
  }

  svg {
    width: 1.5rem;
    height: auto;
  }
`;

type TextAndFlipCardProps = {
  $cardAlignment?: string;
};

export const TextAndFlipCardContainer = styled.div<TextAndFlipCardProps>`
  display: flex;
  ${({ $cardAlignment }) =>
    $cardAlignment === "right" && "justify-content: space-between;"}
  flex-direction: row-reverse;
  gap: 2rem;

  ${({ $cardAlignment }) => {
    if ($cardAlignment === "left") {
      return css`
        flex-direction: row;
      `;
    }
  }}

  @media (max-width: 700px) {
    flex-direction: column-reverse;
    align-items: center;
  }
`;

type TextAndImageCardProps = {
  $cardAlignment?: string;
};

export const TextAndImageCardContainer = styled.div<TextAndImageCardProps>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: "text image";
  gap: 2rem;

  > img {
    grid-area: image;
  }

  > div {
    grid-area: text;
  }

  ${({ $cardAlignment }) => {
    if ($cardAlignment === "left") {
      return css`
        grid-template-areas: "image text";
      `;
    }
  }}

  @media (max-width: 700px) {
    display: flex;
    flex-direction: column-reverse;
  }
`;

type WrapperProps = {};
export const Wrapper = styled.div<WrapperProps>`
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 3em;

  padding-bottom: 40px;
  overflow-x: hidden;

  .bg-gray {
    background-color: ${vars.colors.gray100};
  }

  h1 {
    margin-top: 1rem !important;
  }

  h1,
  h2,
  h3 {
    font-size: 1.6rem;
  }

  h4 {
    font-size: 1.3rem;
  }

  p {
    font-size: 1rem;
    line-height: 1.5;
    .ql-size-small {
      font-size: 0.75em;
    }

    .ql-size-normal {
      font-size: 1em;
    }

    .ql-size-large {
      font-size: 1.2em;
    }

    & .ql-size-huge {
      font-size: 1.6em;
    }

    @media (max-width: 600px) {
      font-size: 0.8em;

      .ql-size-small {
        font-size: 0.6em;
      }

      .ql-size-normal {
        font-size: 0.8em;
      }

      .ql-size-large {
        font-size: 1em;
      }

      & .ql-size-huge {
        font-size: 1.3em;
      }
    }
  }

  p ~ ul {
    margin-top: 0 !important;
  }

  li {
    font-size: 1rem;
    line-height: 1.1;
  }

  h1,
  h2,
  h3,
  h4,
  p {
    margin-bottom: 1.2rem;
    margin-top: 0;
  }

  h1 {
    font-family: ${vars.fontTertiary};
    color: ${vars.colors.black800};
    font-weight: normal;
  }

  h2,
  h3 {
    font-family: ${vars.fontTertiary};
    color: ${vars.colors.black800};
    font-weight: 800;
  }

  h4 {
    font-family: ${vars.fontTertiary};
    color: ${vars.colors.black800};
  }

  p {
    font-family: ${vars.fontPrimary};
    font-weight: 400;
  }

  .row {
    display: grid;
    width: 100%;
    &--1fr-2fr {
      @media screen and (min-width: 800px) {
        grid-template-columns: 1fr 2.4fr;
      }
    }
  }

  .col {
    position: relative;
  }
`;

type IntroProps = {};
export const Intro = styled.div<IntroProps>`
  p {
    strong {
      display: block;
    }

    font-family: ${vars.fontPrimary};
    color: ${vars.colors.black};
  }

  ul.no-styling {
    padding-left: 20px;
    margin-top: -20px;

    li {
      font-weight: 300;
      margin-left: 1.1rem;
    }
  }

  ul {
    li {
      position: relative;
      font-family: ${vars.fontPrimary};
      margin: 36px 0;
      font-weight: 300;
    }
  }
`;

type ListItemProps = {};
export const ListItemWrapper = styled.ul<ListItemProps>``;

type BannerProps = {
  imgSrc: string;
  variant: "primary" | "secondary";
};

export const BannerWrapper = styled.div<BannerProps>`
  height: ${({ variant }) => (variant === "primary" ? "320px" : "auto")};
  width: 100%;
  padding: 5em 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: ${vars.colors.white};
  background-image: ${({ imgSrc, variant }) => `url(${imgSrc})`};

  @media (min-width: 650px) {
    background-position: 50% 20%;
  }
`;

type DeepDiveSectionProps = {};

export const DeepDiveSectionWrapper = styled.div<DeepDiveSectionProps>`
  display: grid;
  grid-template-columns: 1fr;
  font-family: ${vars.fontPrimary};
  row-gap: 2em;
  color: ${vars.colors.black750};
  @media screen and (min-width: 802px) {
    grid-template-columns: 1fr 1fr;
    grid-row: 1 / 2;
    column-gap: 2em;
  }

  img {
    margin: 0;
    vertical-align: middle;
  }
  div {
    @media screen and (min-width: 802px) {
      grid-column-start: 1;
      grid-column-end: span 2;
    }
  }
`;

export const Container = styled.div``;

type ComponentWrapperProps = {
  variant?: string;
  $backgroundColor?: string;
  $noPadding?: boolean;
};

export const ComponentWrapper = styled.div<ComponentWrapperProps>`
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor && $backgroundColor};
  max-width: 1440px;
  margin: 0 auto;
  padding: 3em;
  padding-block: ${({ $noPadding }) => ($noPadding ? "1em" : "3em")};

  @media (max-width: 1000px) {
    padding-inline: 2em;
  }

  @media (max-width: 700px) {
    padding-inline: 1em;
  }

  ${({ variant }) => {
    if (variant === "center") {
      return `
        text-align: center;
      `;
    }
    return ``;
  }}
`;

type DebriefAtRiskProps = {};

export const DebriefAtRiskWrapper = styled.div<DebriefAtRiskProps>`
  font-family: ${vars.fontPrimary};

  .img-card {
    max-width: 285px;
    width: 100%;
    height: auto;
    overflow: hidden;

    img {
      width: 100%;
      height: auto;
      vertical-align: middle;
    }
  }

  .group-quote {
    position: absolute;
    right: 0;
    top: 0;
    transform: translateY(-20%);
    z-index: 1;
    .quote {
      &:first-child {
        margin-right: 10px;
      }
    }

    img {
      width: 44px;
      opacity: 0.8;
    }
  }

  & h3 {
    font-family: ${vars.fontTertiary};
    color: ${vars.colors.green};
    margin-bottom: 50px;
  }

  p {
    margin-left: 8px;
    margin-bottom: 0.8em;
    line-height: 1.5;
  }
`;

export const TitleWrapper = styled.div``;

type LinkTextWrapperProps = {
  $titleColor?: string;
  $linkBackgroundColor?: string;
  $linkTextColor?: string;
};

export const TitleLinkWrapper = styled.div<LinkTextWrapperProps>`
  a {
    padding: 0.5em 2em;
    padding-inline: 20px;
    min-height: 44px;
    background-color: ${({ $linkBackgroundColor }) => $linkBackgroundColor};
    color: ${({ $linkTextColor }) => $linkTextColor};
    font-family: ${vars.fontPrimary};
    display: inline-block;
    font-weight: bold;
    cursor: pointer;
    position: relative;

    &:hover {
      opacity: 0.8;
    }
  }

  p {
    color: ${({ $titleColor }) => $titleColor};
  }
`;

export const UserAnswerWrapper = styled.div``;

export const UserContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1rem;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

export const AnswerText = styled.div<{ client?: string; $textColor?: string }>`
  p {
    color: ${({ $textColor }) => $textColor || vars.colors.black};
  }
`;

export const ReplaySceneWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: 800px) {
    justify-content: flex-start;
  }
`;

export const ReplayScene = styled.div`
  position: relative;
  z-index: 1;
  width: 21rem;
  height: 13rem;

  @media (max-width: 450px) {
    width: 100%;
  }
`;

type SpacerProps = {
  backgroundImage: string;
};

export const Spacer = styled.div<SpacerProps>`
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  width: 21rem;
  height: 13rem;

  @media (max-width: 450px) {
    width: 100%;
  }
`;

export const ButtonDivider = styled.span`
  margin-right: 1rem;
`;

export const ReplayButton = styled.button`
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 0.6rem 1.3rem;
  background-color: rgba(255, 255, 255, 0.8);
  color: ${vars.colors.black};
  font-family: ${vars.fontPrimary};
  font-weight: bold;
  font-size: 1rem;

  & > span:first-child {
    padding-top: 0.1rem;
  }

  & > span:last-child {
    display: inline-block;
    background-image: url("assets/replay_icon.svg");
    height: 1rem;
    width: 1rem;
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    margin-left: 0.7rem;
  }
`;

export const ButtonContainer = styled.div``;

export const FlexContainer = styled.div`
  display: flex;
  gap: 2rem;

  & > div {
    flex: 1;
  }
`;

export const TitleSize = styled.h2<{ $size: string }>`
  font-size: 1.6em;
  font-weight: 600;
  margin-bottom: 1rem;

  ${({ $size }) => {
    if ($size === "small") {
      return css`
        font-size: 1.3em;

        @media (max-width: 600px) {
          font-size: 1em;
        }
      `;
    }

    if ($size === "large") {
      return css`
        font-size: 1.9em;

        @media (max-width: 600px) {
          font-size: 1.6em;
        }
      `;
    }
  }}
`;

export const FlipCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;

  & > button {
    max-width: 250px;
    height: auto;
  }
`;
